/** @file puppetMethods: puppets — list, create, get, template clip import, and the two-step trash → purge. */
import type { PuppetCreateBody, PuppetDoc, PuppetKind } from '../types/rest';
import type { PuppetCreateReply, PuppetListEntry, PuppetListReply, TemplateClipsImportBody, TemplateClipsImportReply } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';

/** The `puppets` member of the client: a puppet is one character, enemy, weapon or prop of a project. */
export type PuppetMethods = {
  /** `GET /api/v1/puppets`: live puppets (one project's with `project`), or the trash with `trashed: true`. */
  list: (q?: { project?: string; kind?: PuppetKind; trashed?: boolean }) => Promise<PuppetListEntry[]>;
  /** `POST /api/v1/puppets`; `template` + `scope` build it, warnings include `SCOPE_DEFAULTED` without a scope. */
  create: (body: PuppetCreateBody) => Promise<PuppetCreateReply>;
  /** `GET /api/v1/puppets/{id}`: the whole document. */
  get: (id: string) => Promise<PuppetDoc>;
  /** `POST /api/v1/puppets/{id}/template-clips`: the template's clips (all, or `clips`) imported in one batch (T-343). */
  templateClipsImport: (id: string, body?: TemplateClipsImportBody) => Promise<TemplateClipsImportReply>;
  /** `DELETE /api/v1/puppets/{id}`: moves the puppet to the trash. */
  trash: (id: string) => Promise<void>;
  /** `POST /api/v1/puppets/{id}/restore`: back from the trash (its project must be live). */
  restore: (id: string) => Promise<void>;
  /** `POST /api/v1/puppets/{id}/purge`: deletes a trashed puppet for good (`NOT_TRASHED` otherwise). */
  purge: (id: string) => Promise<void>;
};

const PUPPET = '/api/v1/puppets/{id}';

/** Puppet calls over `opts`; trash and purge are separate calls by design (never chained here). */
export function puppetMethods(opts: ClientOptions): PuppetMethods {
  const empty = async (method: 'POST' | 'DELETE', template: string, id: string): Promise<void> => {
    await request(opts, { method, path: fillPath(template, { id }), accept: 'empty' });
  };
  return {
    list: async (q = {}) => {
      const query = { project: q.project, kind: q.kind, trashed: q.trashed === undefined ? undefined : String(q.trashed) };
      return (await request<PuppetListReply>(opts, { method: 'GET', path: '/api/v1/puppets', query, accept: 'json' })).value.puppets;
    },
    create: async (body) => (await request<PuppetCreateReply>(opts, { method: 'POST', path: '/api/v1/puppets', body, accept: 'json' })).value,
    get: async (id) => (await request<PuppetDoc>(opts, { method: 'GET', path: fillPath(PUPPET, { id }), accept: 'json' })).value,
    templateClipsImport: async (id, body = {}) => (await request<TemplateClipsImportReply>(opts, {
      method: 'POST', path: fillPath(`${PUPPET}/template-clips`, { id }), body, accept: 'json',
    })).value,
    trash: (id) => empty('DELETE', PUPPET, id),
    restore: (id) => empty('POST', `${PUPPET}/restore`, id),
    purge: (id) => empty('POST', `${PUPPET}/purge`, id),
  };
}
